import React, { useRef, useState } from 'react';
import { FileDetails, Recipient } from './BulkOrderContext';
import { csvFileToRecipients } from './csv-file-to-recipients';
import FileUploadSuccess from './FileUploadSuccess';
import { formatSize } from 'common/utils/utils';

interface FileUploadProps {
    fileDetails: FileDetails;
    setRecipients: (recipients: Recipient[]) => void;
    setError: (error: string) => void;
    setFileDetails: (fileDetails: FileDetails) => void;
    maxRecipients?: number;
    maxFileSize?: number;
}

export const FileUpload = ({
    fileDetails,
    setFileDetails,
    setRecipients,
    setError,
    maxRecipients = 125,
    maxFileSize = 0,
}: FileUploadProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            handleFileUpload(event.target.files[0]);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleFileUpload = async (file: File) => {
        try {
            setError('');

            if (file.size > maxFileSize && maxFileSize > 0) {
                setError(
                    `The maximum file size is ${formatSize(
                        maxFileSize
                    )}. Please upload a smaller file.`
                );
                return;
            }

            if (!file.name.toLowerCase().endsWith('.csv')) {
                setError(
                    'Unfortunately that file type is not supported. Please try uploading a .csv file.'
                );
                return;
            }

            const recipients = await csvFileToRecipients(file);

            if (recipients.length > maxRecipients && maxRecipients > 0) {
                setError(
                    `Each bulk order is limited to ${maxRecipients} gifts. Please adjust your file accordingly and re-upload.`
                );
                return;
            }

            // TODO(ParkingLot): Empty CSV files?
            if (recipients.length <= 0) {
                setError(
                    `Your file seems to be empty. Please adjust your file accordingly and re-upload.`
                );
                return;
            }

            setRecipients(recipients);
            setUploadedFile(file);
            setFileDetails({ name: file.name, size: file.size });
        } catch (error) {
            const errorMsg =
                typeof error === 'string'
                    ? error
                    : 'There was an error trying to upload your file. If the issue persists, please reach out to customer support at 800-727-4715 (Option 3).';
            setError(errorMsg);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);

        if (event.dataTransfer.files) {
            handleFileUpload(event.dataTransfer.files[0]);
        }
    };

    return fileDetails ? (
        <FileUploadSuccess
            completed={!uploadedFile}
            fileName={fileDetails.name}
            fileSize={fileDetails.size}
        />
    ) : (
        <div
            className={`relative w-full h-36 rounded-2xl border border-dashed ${
                isDragging
                    ? 'border-brand bg-pale-blue border-dashed border-2 border-brand rounded-8'
                    : 'border-light-grey'
            } p-6 cursor-pointer flex items-center justify-center
            hover:bg-pale-blue hover:border-dashed hover:border-2 hover:border-brand hover:rounded-8`}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileInputChange}
                accept=".csv"
            />
            <div className="text-center">
                <p className="text-base font-semibold text-grey-darkest">
                    Drag & drop a file or{' '}
                    <span className="font-bold underline text-brand">choose file</span>
                </p>
            </div>
        </div>
    );
};

export default FileUpload;
